// Error response types
interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

// Centralized error message system
export const ERROR_MESSAGES = {
  // Network & Connection Errors
  NETWORK_ERROR: 'Connection lost. Please check your internet connection and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',

  // Authentication Errors
  AUTH_ERROR: 'Session expired. Please login again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. Please contact administrator.',

  // Contract/Blockchain Errors
  CONTRACT_ERROR: 'Blockchain connection failed. Please check your wallet connection.',
  METAMASK_ERROR: 'MetaMask connection failed. Please ensure MetaMask is installed and connected.',
  TRANSACTION_FAILED: 'Transaction failed. Please try again.',
  USER_DENIED_TRANSACTION: 'Transaction cancelled by user.',
  INSUFFICIENT_FUNDS: 'Insufficient funds for this transaction.',
  CONTRACT_NOT_FOUND: 'Smart contract not found. Please try again later.',

  // Validation Errors
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_NUMBER: 'Please enter a valid number.',
  INVALID_ADDRESS: 'Please enter a valid wallet address.',

  // API Errors
  FETCH_FAILED: 'Failed to load data. Please try again.',
  SAVE_FAILED: 'Failed to save changes. Please try again.',
  DELETE_FAILED: 'Failed to delete item. Please try again.',
  UPDATE_FAILED: 'Failed to update item. Please try again.',

  // File Upload Errors
  FILE_TOO_LARGE: 'File size exceeds the maximum limit.',
  INVALID_FILE_TYPE: 'Invalid file type. Please select a supported file format.',
  UPLOAD_FAILED: 'File upload failed. Please try again.',

  // Search Errors
  SEARCH_FAILED: 'Search failed. Please try again.',
  NO_RESULTS: 'No results found for your search.',

  // Generic Errors
  GENERIC_ERROR: 'Something went wrong. Please try again.',
  UNKNOWN_ERROR: 'An unknown error occurred. Please contact support if this persists.',
} as const;

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;

// Helper function to get error message
export const getErrorMessage = (key: ErrorMessageKey): string => {
  return ERROR_MESSAGES[key];
};

// Helper function to get error message from API response
export const getApiErrorMessage = (error: ApiError): string => {
  // Handle different error response formats
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  // Map common HTTP status codes
  if (error?.response?.status) {
    switch (error.response.status) {
      case 400:
        return ERROR_MESSAGES.GENERIC_ERROR;
      case 401:
        return ERROR_MESSAGES.AUTH_ERROR;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return 'Resource not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        return ERROR_MESSAGES.GENERIC_ERROR;
    }
  }

  return ERROR_MESSAGES.GENERIC_ERROR;
};

/** Normalize any error to a string message (handles Error, ResponseError, and objects with message). */
function getErrorMessageFromUnknown(error: unknown): string {
  if (error == null) return ERROR_MESSAGES.UNKNOWN_ERROR;
  if (typeof error === 'string') return error;
  const err = error as { message?: string; error?: { message?: string }; data?: string };
  return err?.message ?? err?.error?.message ?? err?.data ?? ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * User-friendly message for wallet/transaction errors (MetaMask denial, RPC errors, etc.).
 * Use this when handling contract.send() rejections or transaction errors.
 */
export function getTransactionErrorMessage(error: unknown): string {
  const msg = getErrorMessageFromUnknown(error);
  const lower = msg.toLowerCase();

  if (
    lower.includes('user denied') ||
    lower.includes('denied transaction') ||
    lower.includes('user rejected') ||
    lower.includes('rejected by user')
  ) {
    return ERROR_MESSAGES.USER_DENIED_TRANSACTION;
  }

  if (lower.includes('insufficient funds') || lower.includes('insufficient balance')) {
    return ERROR_MESSAGES.INSUFFICIENT_FUNDS;
  }

  if (lower.includes('rate limit') || lower.includes('-32005')) {
    return 'Request rate limited. Please wait 30–60 seconds and try again.';
  }

  if (lower.includes('network') || lower.includes('connection')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (lower.includes('timeout')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  return msg || ERROR_MESSAGES.TRANSACTION_FAILED;
}

// Helper function to get user-friendly error message for common scenarios
export const getUserFriendlyError = (error: ApiError | Error | unknown): string => {
  const msg = getErrorMessageFromUnknown(error);
  const lower = msg.toLowerCase();

  if (
    lower.includes('user denied') ||
    lower.includes('denied transaction') ||
    lower.includes('user rejected')
  ) {
    return ERROR_MESSAGES.USER_DENIED_TRANSACTION;
  }

  if (lower.includes('insufficient funds')) {
    return ERROR_MESSAGES.INSUFFICIENT_FUNDS;
  }

  if (lower.includes('network')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (lower.includes('timeout')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  // Fall back to API error message for ApiError-shaped errors
  return getApiErrorMessage(error as ApiError);
};
